import { ELoadingType, EMGirlType, Loading, MgirlNotice } from '@bilibili-maoer/prelude'

import { useMemo } from 'react'
import { useMount } from 'react-use'

import { Pagination, Stack, Typography, styled } from '@mui/material'
import { match } from 'ts-pattern'

import { AnnouncementItem } from './item'
import { EAnnouncementType } from './types'

import { StyledPaper as Paper } from '~/core/components/layout'
import { Title, TitleChildren } from '~/core/components/title'
import { asMissEvanPagination, defaultPageSize } from '~/core/pagination'
import { NewsApi } from '~/services/web/news'
import useAppSearchParam from '~/shared/navigation/use-app-search-params'

const StyledPaper = styled(Paper)`
  width: 968px;
  margin: 0 auto;
  @media screen and (min-width: 1400px) {
    width: 1140px;
  }
`
const StyledContainer = styled(Stack)`
  height: 100%;
  padding-bottom: 50px;
`
const StyledPaginationContainer = styled('div')`
  display: flex;
  justify-content: center;
`
const FormattedMgirlNotFound = styled(MgirlNotice)`
  margin-top: 140px;
`

export const AnnouncementsList = ({
  tagId,
  pageTitle,
  documentTitle,
  detailPageBaseRoute,
}: {
  tagId: EAnnouncementType
  pageTitle: string
  documentTitle?: TitleChildren
  detailPageBaseRoute: string
}) => {
  const [searchParams, setSearchParams] = useAppSearchParam()
  const page = parseInt(searchParams.get('page') || '1') || 1
  const pageNumber = Number.isNaN(page) ? 1 : page
  const params = useMemo(() => ({ tag_id: tagId, page: pageNumber, page_size: defaultPageSize }), [pageNumber, tagId])
  const [getLiveData, liveData] = NewsApi.getLive.useLazyQuery()
  const { data, isFetching } = liveData
  const pagination = data?.info.pagination && asMissEvanPagination(data.info.pagination)
  const announcementList = data?.info?.Datas || data?.info?.data
  const maxPage = pagination?.count && pagination?.pagesize && Math.ceil(pagination.count / pagination.pagesize)

  function handlePageChange(page: number) {
    getLiveData({ ...params, page: page || 1 })
    setSearchParams({
      page: page.toString() || '1',
    })
  }
  useMount(() => {
    getLiveData(params)
  })

  return (
    <StyledPaper>
      {documentTitle && <Title>{documentTitle}</Title>}
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: '700',
          fontSize: '24px',
          marginBottom: '20px',
          // 和 detail 的 TitleLink 的 line-height 保持一致，否则会出现弹闪
          lineHeight: '36px',
        }}
      >
        {pageTitle}
      </Typography>
      {match(isFetching)
        .with(true, () => <Loading type={ELoadingType.Cat} />)
        .otherwise(() =>
          announcementList?.length ? (
            <StyledContainer spacing={'30px'}>
              {announcementList.map((announcement) => (
                <AnnouncementItem
                  key={announcement.id}
                  announcement={{ ...announcement, to: `${detailPageBaseRoute}/${announcement.id}` }}
                />
              ))}
              <StyledPaginationContainer>
                <Pagination
                  page={pageNumber}
                  count={maxPage}
                  showFirstButton
                  showLastButton
                  onChange={(_, page) => handlePageChange(page)}
                />
              </StyledPaginationContainer>
            </StyledContainer>
          ) : (
            <FormattedMgirlNotFound image={EMGirlType.NotFound} description="目前还没有公告~" />
          )
        )}
    </StyledPaper>
  )
}
